import { ReactElement, useEffect } from "react";

import { useCustomVariation } from "@/hooks/useCustomVariation";
import { SafeGTMProperties } from "goi_common";

/** Hackle이 내려주는 처치군. "Z"는 미할당 또는 로딩 중을 뜻한다 */
export type ExperimentVariation = "A" | "B" | "C" | "D";

interface HackleExperimentContainerProps {
  experimentKey: number;
  /**
   * 처치군별 화면. A는 대조군이라 필수다.
   *
   * 정의하지 않은 처치군이 할당되면 A로 떨어진다. 처치를 잘못 노출하느니
   * 대조군을 보여주는 편이 안전하기 때문이다.
   * 두 처치가 같은 화면이어야 한다면 같은 엘리먼트를 양쪽에 넘긴다.
   */
  variants: Partial<Record<ExperimentVariation, ReactElement>> & { A: ReactElement };
  /**
   * 처치군이 확정된 시점에 호출. 미할당("Z") 상태에서는 호출되지 않는다.
   * 실험 모수 마킹 등 정확한 노출 시점에 부수효과를 걸 때 쓴다.
   */
  onAssigned?: (variation: ExperimentVariation) => void;
  /**
   * experiment_exposure 이벤트 properties에 merge할 추가 컨텍스트.
   * 분석 단계에서 노출자를 세그먼트(예: lead_status)로 쪼개 볼 때 쓴다.
   */
  contextExtra?: SafeGTMProperties;
}

/**
 * Hackle 실험의 처치군에 따라 화면을 고르는 컨테이너.
 *
 * 이 컴포넌트가 마운트되는 순간 Hackle에 노출이 기록되고 할당이 확정된다.
 * 그래서 실험 모수가 아닌 사용자에게는 아예 마운트하지 않아야 한다.
 * 시간대처럼 비동기로 확정되는 조건이 앞에 있다면 TimeSplitContainer로 감싼다.
 *
 * @example
 * <HackleExperimentContainer
 *   experimentKey={328}
 *   variants={{ A: currentScreen, B: currentScreen, C: <NewScreen /> }}
 * />
 */
export const HackleExperimentContainer = ({
  experimentKey,
  variants,
  onAssigned,
  contextExtra,
}: HackleExperimentContainerProps) => {
  const variation = useCustomVariation(experimentKey, undefined, contextExtra);
  const isAssigned = variation !== "Z";

  useEffect(() => {
    if (isAssigned) onAssigned?.(variation as ExperimentVariation);
  }, [isAssigned, variation, onAssigned]);

  // 처치군이 확정되기 전에 한쪽을 보여주면 그 화면이 잘못 노출된다
  if (!isAssigned) return null;

  return variants[variation as ExperimentVariation] ?? variants.A;
};
